import { activeDevice, glyphsFor } from './inputPrompts';
import { actionPrompt, glyphEl, livePrompt } from './prompts';
import type { Sim } from '../sim/sim';
import { clear, fmtNum, fmtTime, h } from './dom';
import { characterDef } from '../content/characters';
import type { ControlsHudMode } from '../meta/save';

/** In-run HUD. Rebuilt cheaply every frame by writing text/width only. */
export class Hud {
  readonly root: HTMLElement;
  private hpBar: HTMLElement;
  private hpText: HTMLElement;
  private xpBar: HTMLElement;
  private levelText: HTMLElement;
  private stage: HTMLElement;
  private timer: HTMLElement;
  private objective: HTMLElement;
  private kills: HTMLElement;
  private shards: HTMLElement;
  private ability: HTMLElement;
  private abilityName: HTMLElement;
  private abilityPips: HTMLElement;
  private fireState: HTMLElement;
  private bossWrap: HTMLElement;
  private bossBar: HTMLElement;
  private bossName: HTMLElement;
  private toasts: HTMLElement;
  private party: HTMLElement;
  private partyRows: { root: HTMLElement; name: HTMLElement; bar: HTMLElement }[] = [];
  private net: HTMLElement;
  private controlsHud: HTMLElement;
  private portalPrompt: HTMLElement;
  private controlsActive = false;
  private controlsEnabled = true;

  constructor(parent: HTMLElement) {
    this.hpBar = h('i');
    this.xpBar = h('i');
    this.bossBar = h('i');
    this.hpText = h('span');
    this.levelText = h('span');
    this.stage = h('div', { class: 'stage' });
    this.timer = h('div', { class: 'timer' });
    this.objective = h('div', { class: 'hud-objective sub' });
    this.kills = h('div');
    this.shards = h('div', { class: 'shards' });
    this.abilityName = h('span', { class: 'sub' });
    this.abilityPips = h('div', { class: 'dash' });
    // Key/button badge for the ability; touch players have the on-screen button instead.
    const abilityKey = livePrompt(() => (activeDevice().kind === 'touch' ? [] : glyphsFor('ability').map(glyphEl)), { class: 'live-prompt ability-key' });
    this.ability = h('div', { class: 'row ability' }, abilityKey, this.abilityName, this.abilityPips);
    this.fireState = h('div', { class: 'firestate' });
    this.bossName = h('div', { class: 'sub', style: 'margin-bottom:4px;font-weight:700' });
    this.bossWrap = h('div', { style: 'display:none' }, this.bossName, h('div', { class: 'bar boss', style: 'height:14px' }, this.bossBar));
    this.toasts = h('div');
    this.party = h('div', { class: 'hud-party', style: 'display:none' });
    this.net = h('div', { class: 'hud-net', style: 'display:none' });
    const zoomPrompt = livePrompt(() => {
      if (activeDevice().kind === 'touch') return [h('span', { class: 'prompt-label' }, 'Pinch · Zoom')];
      return [...glyphsFor('zoomIn').map(glyphEl), ...glyphsFor('zoomOut').map(glyphEl), h('span', { class: 'prompt-label' }, 'Zoom')];
    }, { class: 'live-prompt action-prompt' });
    this.portalPrompt = livePrompt(() => {
      if (activeDevice().kind === 'touch') return [h('span', { class: 'prompt-label' }, 'Walk into a portal')];
      return [...glyphsFor('move').map(glyphEl), h('span', { class: 'prompt-label' }, 'Enter portal')];
    }, { class: 'live-prompt action-prompt hud-portal-prompt', style: 'display:none' });
    this.controlsHud = h('div', { class: 'hud-controls', 'aria-label': 'In-game controls' }, actionPrompt('move', 'Move'), actionPrompt('ability', 'Ability'), actionPrompt('pause', 'Pause'), zoomPrompt, this.portalPrompt);
    this.root = h(
      'div',
      { id: 'hud' },
      h(
        'div',
        { class: 'tl' },
        h('div', { class: 'row', style: 'justify-content:space-between' }, h('span', null, 'HP'), this.hpText),
        h('div', { class: 'bar hp' }, this.hpBar),
        h('div', { class: 'row', style: 'justify-content:space-between' }, this.levelText, h('span', { class: 'sub' }, 'XP')),
        h('div', { class: 'bar xp' }, this.xpBar),
        this.ability,
        this.fireState,
        this.party,
      ),
      h('div', { class: 'tc' }, this.stage, this.timer, this.objective),
      h('div', { class: 'tr' }, this.kills, this.shards, this.net),
      h('div', { class: 'bc' }, this.bossWrap),
      this.controlsHud,
      this.toasts,
    );
    parent.appendChild(this.root);
  }

  toast(text: string): void {
    const el = h('div', { class: 'toast' }, text);
    this.toasts.appendChild(el);
    setTimeout(() => el.remove(), 2300);
  }

  /** Index of the player this HUD describes (online co-op: the local player). */
  localPlayer = 0;

  /** Online co-op: show every other party member's name and health (null hides the panel). */
  setParty(names: string[] | null): void {
    clear(this.party);
    this.partyRows = [];
    this.party.style.display = names && names.length > 1 ? '' : 'none';
    if (!names) return;
    names.forEach((name) => {
      const bar = h('i');
      const label = h('span', null, name);
      const root = h('div', { class: 'hud-party-row' }, label, h('div', { class: 'bar hp' }, bar));
      this.partyRows.push({ root, name: label, bar });
      this.party.appendChild(root);
    });
  }

  /** Online co-op connection line (ping / waiting state); null hides it. */
  setNetStatus(text: string | null, warn = false): void {
    this.net.style.display = text ? '' : 'none';
    this.net.textContent = text ?? '';
    this.net.classList.toggle('warn', warn);
  }

  update(sim: Sim): void {
    const local = sim.world.players[this.localPlayer] ? this.localPlayer : 0;
    const p = sim.world.players[local];
    const s = sim.getStats(local);
    const r = sim.run;
    this.portalPrompt.style.display = r.portals.length ? '' : 'none';
    this.partyRows.forEach((row, i) => {
      const member = sim.world.players[i];
      row.root.style.display = i === local || !member ? 'none' : '';
      if (!member) return;
      row.root.classList.toggle('down', !member.alive);
      row.bar.style.width = `${Math.max(0, (member.hp / sim.getStats(i).maxHp) * 100)}%`;
    });
    this.hpBar.style.width = `${Math.max(0, (p.hp / s.maxHp) * 100)}%`;
    this.hpText.textContent = `${Math.ceil(p.hp)} / ${Math.round(s.maxHp)}`;
    this.xpBar.style.width = `${Math.min(100, (r.xp / r.xpToNext) * 100)}%`;
    this.levelText.textContent = `Level ${r.level}`;
    const era = sim.currentEra;
    const st = sim.currentStage;
    this.stage.textContent = `${era.name} · ${st.name}`;
    const objective = st.objective;
    const surviveFor = objective?.type === 'survive' ? objective.seconds ?? st.duration : st.duration;
    const remaining = st.boss ? r.stageTime : Math.max(0, surviveFor - r.stageTime);
    const objectiveHealthPct = objective?.type === 'defend' || objective?.type === 'escort' ? Math.ceil((r.objectiveHealth / (objective.health ?? 100)) * 100) : 0;
    this.timer.textContent = objective?.type === 'kill' ? `${r.stageKills} / ${objective.count}` : objective?.type === 'waves' ? `${r.waveCursor} / ${st.waves.length}` : objective?.type === 'hold' ? `${Math.floor(r.objectiveProgress)} / ${objective.seconds}s` : objective?.type === 'collect' || objective?.type === 'hunt' ? `${Math.floor(r.objectiveProgress)} / ${objective.count}` : objective?.type === 'defend' ? `${fmtTime(Math.max(0, objective.seconds - r.stageTime))} · ${objectiveHealthPct}%` : objective?.type === 'escort' ? `${Math.floor(r.objectiveProgress)} / ${objective.distance} · ${objectiveHealthPct}%` : fmtTime(remaining);
    this.objective.textContent = st.boss ? 'Defeat the boss' : objective?.label ?? (objective?.type === 'kill' ? 'Eliminate enemies' : objective?.type === 'waves' ? 'Clear every wave' : objective?.type === 'hold' ? 'Hold the marked zone' : objective?.type === 'collect' ? 'Collect temporal fragments' : objective?.type === 'hunt' ? 'Hunt priority targets' : objective?.type === 'defend' ? 'Defend the temporal anchor' : objective?.type === 'escort' ? 'Escort the chronocore' : 'Survive');
    this.kills.textContent = `Kills ${fmtNum(r.kills)}`;
    this.shards.textContent = `◆ ${r.shardsPickedUp}`;
    const character = characterDef(sim.ctx.characters[local]);
    const charges = character.id === 'knight' ? s.dashCharges : character.ability.charges;
    const cooldown = character.id === 'knight' ? s.dashCooldown : character.ability.cooldown;
    this.abilityName.textContent = character.ability.name;
    if (this.abilityPips.childElementCount !== charges) {
      clear(this.abilityPips);
      for (let i = 0; i < charges; i++) this.abilityPips.appendChild(h('i'));
    }
    for (let i = 0; i < this.abilityPips.children.length; i++) {
      const pip = this.abilityPips.children[i] as HTMLElement;
      const charged = i < p.dashChargesLeft;
      const charging = i === p.dashChargesLeft && p.dashChargesLeft < charges;
      pip.classList.toggle('on', charged);
      pip.classList.toggle('charging', charging);
      const progress = charging ? Math.max(0, Math.min(1, 1 - p.dashCdT / cooldown)) : 0;
      pip.style.setProperty('--dash-charge', `${(progress * 100).toFixed(1)}%`);
    }
    const firing = !p.moving && p.dashT <= 0;
    this.fireState.textContent = firing ? (p.targetEnemy ? 'firing' : 'ready') : s.movingFireMult > 0 ? 'moving · reduced fire' : 'moving · no fire';
    this.fireState.className = 'firestate ' + (firing ? 'firing' : 'moving');
    const boss = sim.ctx.boss;
    if (boss) {
      const e = sim.world.enemies;
      this.bossWrap.style.display = '';
      this.bossName.textContent = `${boss.def.name} — ${boss.def.phases[e.phase[boss.slot]].name ?? 'Phase ' + (e.phase[boss.slot] + 1)}`;
      this.bossBar.style.width = `${Math.max(0, (e.hp[boss.slot] / e.maxHp[boss.slot]) * 100)}%`;
    } else this.bossWrap.style.display = 'none';
  }

  setVisible(v: boolean): void {
    this.root.style.display = v ? '' : 'none';
  }

  setControlsMode(mode: ControlsHudMode, completedRuns: number): void {
    this.controlsEnabled = mode === 'always' || (mode === 'firstRuns' && completedRuns < 3);
    this.refreshControlsVisibility();
  }

  setControlsActive(active: boolean): void {
    this.controlsActive = active;
    this.refreshControlsVisibility();
  }

  private refreshControlsVisibility(): void {
    this.controlsHud.style.display = this.controlsActive && this.controlsEnabled ? '' : 'none';
  }
}
